import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { learningAPI } from '../api/client';
import Breadcrumb from '../components/Breadcrumb';
import { 
  BookOpen, Clock, CheckCircle2, Circle, 
  Play, Trophy, TrendingUp, ChevronRight,
  Calendar, Target
} from 'lucide-react';

const MyProgress = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEnrolled: 0,
    inProgress: 0,
    completed: 0,
    totalLessonsCompleted: 0
  });

  useEffect(() => {
    document.title = 'İlerlemem | İlkadım360 Hub';
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    try {
      const response = await learningAPI.getMyEnrollmentsDetailed();
      setEnrollments(response.data);
      
      // Calculate stats
      const completed = response.data.filter(e => e.isCompleted).length;
      const totalLessons = response.data.reduce((acc, e) => acc + e.completedLessons, 0);
      
      setStats({
        totalEnrolled: response.data.length,
        inProgress: response.data.length - completed,
        completed: completed,
        totalLessonsCompleted: totalLessons
      });
    } catch (error) {
      console.error('Kayıtlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="my-progress-page">
      <Breadcrumb 
        items={[
          { label: 'Online Eğitim', path: '/learning' },
          { label: 'İlerlemem', path: null }
        ]} 
      />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">İlerlemem</h1>
        <p className="text-gray-600">Kayıtlı olduğunuz kurslar ve ilerleme durumunuz</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          icon={BookOpen} 
          label="Kayıtlı Kurs" 
          value={stats.totalEnrolled} 
          color="blue"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Devam Eden" 
          value={stats.inProgress} 
          color="yellow"
        />
        <StatCard 
          icon={Trophy} 
          label="Tamamlanan" 
          value={stats.completed} 
          color="green"
        />
        <StatCard 
          icon={CheckCircle2} 
          label="Tamamlanan Ders" 
          value={stats.totalLessonsCompleted} 
          color="purple"
        />
      </div>

      {/* Enrollments List */}
      {enrollments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <BookOpen className="mx-auto text-gray-300 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Henüz kursa kayıt olmadınız</h3>
          <p className="text-gray-500 mb-6">Online eğitimlere göz atın ve öğrenmeye başlayın</p>
          <button
            onClick={() => navigate('/learning')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Kursları Keşfet
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {enrollments.map((item) => (
            <EnrollmentCard 
              key={item.enrollment.id} 
              data={item} 
              onClick={() => navigate(`/learning/progress/${item.course.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${colors[color]}`}>
          <Icon size={22} />
        </div>
      </div>
    </div>
  );
};

// Enrollment Card Component
const EnrollmentCard = ({ data, onClick }) => {
  const { enrollment, course, totalLessons, completedLessons, progressPercent, isCompleted } = data;
  
  const getStatusBadge = () => {
    if (isCompleted) {
      return (
        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
          <Trophy size={14} />
          Tamamlandı
        </span>
      );
    }
    if (progressPercent > 0) {
      return (
        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium flex items-center gap-1">
          <TrendingUp size={14} />
          Devam Ediyor
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium flex items-center gap-1">
        <Circle size={14} />
        Başlanmadı
      </span>
    );
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer group"
      onClick={onClick}
      data-testid={`enrollment-card-${course.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
              {course.title}
            </h3>
            {getStatusBadge()}
          </div>
          <p className="text-gray-600 text-sm line-clamp-2">{course.description}</p>
        </div>
        <ChevronRight className="text-gray-400 group-hover:text-blue-600 transition-colors ml-4" size={24} />
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600">İlerleme</span>
          <span className="font-semibold text-gray-900">{progressPercent}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full transition-all duration-500 ${
              isCompleted ? 'bg-green-500' : progressPercent > 0 ? 'bg-blue-500' : 'bg-gray-300'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex items-center gap-6 text-sm text-gray-500">
        <div className="flex items-center gap-1">
          <CheckCircle2 size={16} className={isCompleted ? 'text-green-500' : 'text-gray-400'} />
          <span>{completedLessons} / {totalLessons} ders</span>
        </div>
        <div className="flex items-center gap-1">
          <Calendar size={16} />
          <span>Kayıt: {new Date(enrollment.enrolledAt).toLocaleDateString('tr-TR')}</span>
        </div>
        {enrollment.completedAt && (
          <div className="flex items-center gap-1 text-green-600">
            <Trophy size={16} />
            <span>Tamamlandı: {new Date(enrollment.completedAt).toLocaleDateString('tr-TR')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProgress;
